import React, { useEffect, useLayoutEffect, useMemo, useRef } from "react";

import { AnnotationLayer } from "./AnnotationLayer";
import { useSpatialSegments } from "../../hooks/useSpatialSegments";
import { useVideoLayout } from "../../hooks/overlay/useVideoLayout";
import { usePresentedFrameTime } from "../../hooks/overlay/usePresentedFrameTime";
import { useAnchoredTracking } from "../../tracking/player/useAnchoredTracking";
import { createTrackingSelectionIndex, selectIndexedTrackedSegments } from "../../tracking/player/selection";
import {
  buildTrackingInspectorSnapshot,
  buildTrackingInspectorSummary,
  createTrackingInspectorIndex,
} from "../../tracking/inspector/status";
import { useTrackingInspectorStore } from "../../tracking/inspector/controller";
import { useTimelineDataStore, useTimelineStore } from "../../store/timeline-context";
import { createFocusIndex } from "../../utils/focus";
import { useTrackingTimelinePublication } from "../../tracking/player/useTrackingTimelinePublication";
import type { FrameRecoveryProvider } from "../../tracking/recovery/contracts";

import type { NanoModelUrls } from "../../tracking/contracts";

interface AnnotationsOverlayProps {
  src?: string;
  subscribeToTime?: (callback: (time: number) => void) => () => void;
  seek?: (time: number) => void;
  recoveryProvider?: FrameRecoveryProvider;
  nanoModelUrls?: NanoModelUrls;
  /**
   * Whether any interval is in flight for this source. The host uses it to
   * yield background work to the video someone is actually watching; the
   * equivalent diagnostic attributes exist only in developer mode.
   */
  onTrackingBusyChange?: (busy: boolean) => void;
}

/** Rendering only: decoding and both tracking algorithms run in a dedicated worker. */
export const AnnotationsOverlay: React.FC<AnnotationsOverlayProps> = ({
  src,
  subscribeToTime,
  seek,
  recoveryProvider,
  nanoModelUrls,
  onTrackingBusyChange,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { containerSize, videoLayout, videoElementRef } = useVideoLayout(containerRef);
  const currentTime = usePresentedFrameTime(videoElementRef, src, subscribeToTime);

  const items = useTimelineDataStore((state) => state.items);
  const durationSeconds = useTimelineDataStore((state) => state.durationSeconds);
  const publishTimeline = useTimelineStore((state) => state.setTrackingTimeline);
  const clearTimeline = useTimelineStore((state) => state.clearTrackingTimeline);

  const segments = useSpatialSegments(items);
  const focusIndex = useMemo(() => createFocusIndex(items), [items]);
  const publishDiagnostics = useTrackingInspectorStore((state) => state.publishDiagnostics);
  const developerMode = useTrackingInspectorStore((state) => state.enabled);
  const inspectorOpen = useTrackingInspectorStore((state) => state.open);
  const settings = useTrackingInspectorStore((state) => state.activeSettings);
  const generation = useTrackingInspectorStore((state) => state.generation);
  const trackLabels = useMemo(
    () =>
      new Map(
        segments.flatMap((segment) =>
          segment.trackId && segment.label ? [[segment.trackId, segment.label] as const] : [],
        ),
      ),
    [segments],
  );
  const tracking = useAnchoredTracking(src, items, currentTime, {
    enabled: settings.interpolationEnabled,
    strategies: settings.strategies,
    nanoMode: settings.nanoMode,
    runKey: generation,
    recoveryEnabled: !!settings.recoveryEnabled,
    recoveryProvider,
    nanoModelUrls,
    trackLabels,
  });
  const selectionIndex = useMemo(
    () => createTrackingSelectionIndex(segments, tracking.intervals, tracking.results),
    [segments, tracking.intervals, tracking.results],
  );
  const timelineResetKey = useMemo(() => ({ src, generation }), [src, generation]);
  useTrackingTimelinePublication({
    items,
    index: selectionIndex,
    intervals: tracking.intervals,
    activity: tracking.activity,
    errors: tracking.errors,
    annotationRevision: segments,
    durationSeconds,
    resetKey: timelineResetKey,
    publishTimeline,
    clearTimeline,
  });
  const visible = selectIndexedTrackedSegments(selectionIndex, currentTime);
  const warningCount = useMemo(
    () =>
      developerMode ? [...tracking.results.values()].reduce((count, result) => count + result.warnings.length, 0) : 0,
    [developerMode, tracking.results],
  );
  const index = useMemo(
    () => (developerMode && inspectorOpen ? createTrackingInspectorIndex(items, segments, tracking.intervals) : null),
    [developerMode, inspectorOpen, items, segments, tracking.intervals],
  );
  const busy = tracking.activity.active.size > 0;
  const reportBusy = useRef(onTrackingBusyChange);
  reportBusy.current = onTrackingBusyChange;
  useEffect(() => reportBusy.current?.(busy), [busy]);
  // Switching assets unmounts this player mid-interval, and its last word
  // would otherwise be "busy" — leaving anything that yields to it waiting on
  // a player that no longer exists.
  useEffect(() => () => reportBusy.current?.(false), []);

  const trackingStatus = developerMode ? buildTrackingInspectorSummary({ currentTime, ...tracking }) : null;
  // Publish to a scoped store rather than sending presentation-frame updates
  // through the player. Detailed rows are built only for an open inspector.
  useLayoutEffect(() => {
    if (!trackingStatus) return;
    const snapshot = index
      ? buildTrackingInspectorSnapshot({ currentTime, index, visible, ...tracking, summary: trackingStatus })
      : null;
    publishDiagnostics({ summary: trackingStatus, snapshot });
  });

  const showAnnotations = segments.length > 0 && containerSize.width > 0 && containerSize.height > 0;

  return (
    <>
      <div
        className="pointer-events-none absolute inset-0 z-10"
        data-tracking-errors={developerMode ? tracking.errors.size : undefined}
        data-tracking-pending={trackingStatus?.counts.pending}
        data-tracking-processing={trackingStatus?.counts.processing}
        data-tracking-ready={trackingStatus?.counts.ready}
        data-tracking-status={trackingStatus?.summary}
        data-tracking-warnings={developerMode ? warningCount : undefined}
        data-video-timestamp={developerMode ? currentTime : undefined}
        ref={containerRef}
      >
        {showAnnotations && (
          <AnnotationLayer
            currentTime={currentTime}
            focusIndex={focusIndex}
            segments={visible}
            videoLayout={videoLayout}
            onSeek={seek}
          />
        )}
      </div>
    </>
  );
};

AnnotationsOverlay.displayName = "AnnotationsOverlay";
